import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookMarked, Briefcase, MessageSquare, Crown, Trash2, Sparkles, Search, FileText } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [saved, setSaved] = useState([]);
  const [apps, setApps] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [cbUsage, setCbUsage] = useState(null);
  const [sub, setSub] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const { refreshUser } = useAuth();

  const loadSaved = () => api.get("/saved").then(({ data }) => setSaved(data)).catch(() => {});
  const loadSub = () => api.get("/payments/subscription").then(({ data }) => setSub(data)).catch(() => {});

  useEffect(() => {
    loadSaved();
    loadSub();
    api.get("/my/applications").then(({ data }) => setApps(data)).catch(() => {});
    api.get("/chat/sessions").then(({ data }) => setSessions(data)).catch(() => {});
    api.get("/casebrief/usage").then(({ data }) => setCbUsage(data)).catch(() => {});
  }, []);

  const cancelSub = async () => {
    if (!window.confirm("Cancel your subscription? After cancelling, you may not be able to use the premium features.")) return;
    setCancelling(true);
    try {
      const { data } = await api.post("/payments/cancel-subscription");
      toast.success(data.message || "Subscription cancelled.");
      await refreshUser();
      loadSub();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const removeSaved = async (id) => {
    try {
      await api.delete(`/saved/${id}`);
      setSaved((s) => s.filter((x) => x.id !== id));
      toast.success("Removed.");
    } catch {
      toast.error("Failed.");
    }
  };

  const planName = (key) => {
    if (!key || key === "free") return "Free";
    const base = key.replace(/_(1m|3m|yr)$/, "");
    const names = { student: "Student", advocate: "Advocate", client: "Client", lawfirm: "Law Firm", institution: "Institution" };
    return names[base] || "Premium";
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="dashboard-page">
      {/* Header */}
      <div className="glass-card rounded-3xl p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-full bg-btn text-btnink flex items-center justify-center font-serif text-2xl font-bold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div>
            <h1 className="font-serif text-3xl text-ink">{user?.name}</h1>
            <p className="text-inkfaint text-sm">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] border border-gold/40 text-gold px-4 py-2 rounded-full">
            <Crown className="w-4 h-4" /> {planName(user?.plan)} Plan
          </span>
          {user?.plan === "free" && (
            <Link to="/pricing" data-testid="upgrade-link" className="rounded-full bg-btn text-btnink hover:bg-btnhover px-5 py-2 text-xs font-semibold uppercase tracking-wider transition-all">
              Upgrade
            </Link>
          )}
        </div>
      </div>

      {/* Free-trial prompt for non-subscribers */}
      {user?.plan === "free" && sub && !sub.has_subscription && (
        <div className="glass-card rounded-3xl p-6 mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-gold/40" data-testid="trial-cta-banner">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-gold mt-0.5 shrink-0" />
            <div>
              <h3 className="font-serif text-xl text-ink">
                {sub.trial_used ? "Unlock premium with a monthly plan" : "Start your 1-month free trial"}
              </h3>
              <p className="text-sm text-inkfaint mt-1">
                {sub.trial_used
                  ? "Subscribe to keep using all premium features across every module."
                  : "Get full access to every premium feature free for 30 days — ₹0 today, cancel anytime. Autopay begins only after the trial."}
              </p>
            </div>
          </div>
          <Link to="/pricing" data-testid="start-trial-link" className="shrink-0 rounded-full bg-btn text-btnink hover:bg-btnhover px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-all text-center">
            {sub.trial_used ? "View Plans" : "Start Free Trial"}
          </Link>
        </div>
      )}

      {/* Active trial / subscription management */}
      {sub && sub.has_subscription && (
        <div className="glass-card rounded-3xl p-6 mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="subscription-card">
          <div className="flex items-start gap-3">
            <Crown className="w-6 h-6 text-gold mt-0.5 shrink-0" />
            <div>
              <h3 className="font-serif text-xl text-ink">
                {sub.is_trial ? "Free trial active" : "Subscription active"} — {planName(sub.plan)}
              </h3>
              <p className="text-sm text-inkfaint mt-1" data-testid="subscription-status-text">
                {sub.is_trial
                  ? `No charge today. Autopay starts in ${sub.trial_days_left ?? 30} day${(sub.trial_days_left === 1) ? "" : "s"}${sub.trial_end ? ` (on ${new Date(sub.trial_end).toLocaleDateString("en-IN")})` : ""} unless you cancel.`
                  : "Your monthly autopay is active. You can cancel anytime."}
              </p>
            </div>
          </div>
          <button onClick={cancelSub} disabled={cancelling} data-testid="cancel-subscription-btn" className="shrink-0 rounded-full border border-red-500/40 text-red-500 hover:bg-red-500/10 disabled:opacity-50 px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-all">
            {cancelling ? "Cancelling…" : (sub.is_trial ? "Cancel Trial" : "Cancel Subscription")}
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-5 mt-6">
        <Link to="/justicebot" className="glass-card rounded-2xl p-6 hover:-translate-y-1 transition-all" data-testid="quick-justicebot">
          <Sparkles className="w-7 h-7 text-gold" />
          <h3 className="font-serif text-xl text-ink mt-4">Ask JusticeBot</h3>
          <p className="text-sm text-inkfaint mt-1">AI legal research</p>
        </Link>
        <Link to="/casebrief" className="glass-card rounded-2xl p-6 hover:-translate-y-1 transition-all" data-testid="quick-casebrief">
          <FileText className="w-7 h-7 text-gold" />
          <h3 className="font-serif text-xl text-ink mt-4">CaseBrief AI</h3>
          <p className="text-sm text-inkfaint mt-1" data-testid="dashboard-casebrief-usage">
            {cbUsage?.access
              ? cbUsage.unlimited
                ? "Unlimited briefs"
                : `${cbUsage.remaining} of ${cbUsage.quota} briefs left`
              : "Briefs in seconds"}
          </p>
        </Link>
        <Link to="/careers" className="glass-card rounded-2xl p-6 hover:-translate-y-1 transition-all" data-testid="quick-careers">
          <Briefcase className="w-7 h-7 text-gold" />
          <h3 className="font-serif text-xl text-ink mt-4">Browse Careers</h3>
          <p className="text-sm text-inkfaint mt-1">Internships & jobs</p>
        </Link>
      </div>

      {/* Grid: saved + applications + chats */}
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <section className="glass-card rounded-2xl p-7">
          <h2 className="font-serif text-2xl text-ink flex items-center gap-3"><BookMarked className="w-5 h-5 text-gold" /> Saved Library</h2>
          <div className="mt-5 space-y-3">
            {saved.length === 0 ? (
              <p className="text-sm text-inkfaint font-mono">No saved items yet.</p>
            ) : (
              saved.map((s) => (
                <div key={s.id} className="flex items-center justify-between bg-surface border border-hairline rounded-xl px-4 py-3" data-testid="saved-item">
                  <span className="text-sm text-inkmuted truncate pr-3">{s.title}</span>
                  <button onClick={() => removeSaved(s.id)} className="text-inkfaint hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="glass-card rounded-2xl p-7">
          <h2 className="font-serif text-2xl text-ink flex items-center gap-3"><Briefcase className="w-5 h-5 text-gold" /> My Applications</h2>
          <div className="mt-5 space-y-3">
            {apps.length === 0 ? (
              <p className="text-sm text-inkfaint font-mono">No applications submitted.</p>
            ) : (
              apps.map((a) => (
                <div key={a.id} className="flex items-center justify-between bg-surface border border-hairline rounded-xl px-4 py-3" data-testid="application-item">
                  <span className="text-sm text-inkmuted truncate pr-3">{a.vacancy_title}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-gold shrink-0">{a.status}</span>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="glass-card rounded-2xl p-7 lg:col-span-2">
          <h2 className="font-serif text-2xl text-ink flex items-center gap-3"><MessageSquare className="w-5 h-5 text-gold" /> Research History</h2>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            {sessions.length === 0 ? (
              <p className="text-sm text-inkfaint font-mono">No JusticeBot conversations yet.</p>
            ) : (
              sessions.map((s) => (
                <div key={s.session_id} className="bg-surface border border-hairline rounded-xl px-4 py-3 text-sm text-inkmuted truncate" data-testid="chat-session-item">
                  {s.title}
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
